import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  History,
  RefreshCw,
  ArrowRight,
  Filter,
  Search,
  Download,
  Loader2,
} from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { formatDate } from "@/lib/utils";
import { formatUserLabel, pickParty, shortRef } from "@/lib/formatUser";
import TransferReceiptActions from "@/components/TransferReceiptActions";
import Pagination from "@/components/Pagination";

const CURRENCY_TONES = {
  USD: "bg-[#2BA8F0]",
  CAD: "bg-red-500",
  CLP: "bg-amber-500",
  DOP: "bg-blue-700",
  HTG: "bg-violet-600",
  EUR: "bg-indigo-500",
  BRL: "bg-emerald-600",
  MXN: "bg-teal-600",
};

const KIND_STYLES = {
  local: "bg-emerald-100 text-emerald-700 border-emerald-300",
  internal: "bg-blue-100 text-blue-700 border-blue-300",
  external: "bg-slate-100 text-slate-700 border-slate-300",
};

const KIND_LABEL = {
  local: "Local Transfer",
  internal: "Internal",
  external: "External Funding",
};

const NO_DECIMAL = new Set(["CLP", "HTG"]);
function fmtCash(amount, currency) {
  const noDec = NO_DECIMAL.has(currency);
  const n = Number(amount || 0);
  if (noDec) return `${Math.round(n).toLocaleString("en-US")} ${currency || ""}`.trim();
  return `${n.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${currency || ""}`.trim();
}

function fmtParty(r) {
  if (r._kind === "local") {
    return {
      from: formatUserLabel(pickParty(r, "sender")),
      fromEmail: r.sender_email || "",
      to: formatUserLabel(pickParty(r, "recipient")),
      toEmail: r.recipient_email || "",
    };
  }
  if (r._kind === "internal") {
    const actor = formatUserLabel(pickParty(r, "actor"));
    return {
      from: actor,
      to: `${actor} · (self)`,
      fromEmail: "",
      toEmail: "",
    };
  }
  // external — ledger_entries with FUNDING* or TRANSFER_IN category
  const owner = formatUserLabel(pickParty(r, "owner"));
  const cp = r.counterparty_id ? formatUserLabel(pickParty(r, "counterparty")) : "";
  const cat = r.category || "";
  const isCredit = r.type === "CREDIT";
  // FUNDING_MANUAL / FUNDING_PAYKU / FUNDING_CARD: external → owner
  // TRANSFER_IN / TRANSFER_OUT: counterparty ↔ owner
  if (cat === "TRANSFER_IN") {
    return { from: cp || "—", fromEmail: "", to: owner || "—", toEmail: "" };
  }
  if (cat === "TRANSFER_OUT") {
    return { from: owner || "—", fromEmail: "", to: cp || "—", toEmail: "" };
  }
  // Pure external funding — no user-side counterparty
  const sourceLabel = (cat || "External").replace(/_/g, " ");
  return isCredit
    ? { from: sourceLabel, fromEmail: "", to: owner || "—", toEmail: "" }
    : { from: owner || "—", fromEmail: "", to: sourceLabel, toEmail: "" };
}

export default function FundingHistory() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [scopeAll, setScopeAll] = useState(false);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    kind: "",
    ref: "",
    counterparty: "",
    currency: "",
    date_from: "",
    date_to: "",
  });
  const [skip, setSkip] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const limit = pageSize;

  const canDownload = user?.role !== "COLLABORATOR" ||
    (user?.permissions?.download_transfer_receipts === "allowed" ||
     user?.permissions?.view_transfer_receipts === "allowed");
  const canShare = user?.role !== "COLLABORATOR" ||
    user?.permissions?.share_transfer_receipts === "allowed";

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: String(limit), skip: String(skip) });
      Object.entries(filters).forEach(([k, v]) => { if (v) params.set(k, v); });
      const { data } = await api.get(`/wallet/funding-history/consolidated?${params.toString()}`);
      setRows(data.items || []);
      setTotal(data.total || 0);
      setScopeAll(!!data.scope_all);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [skip, pageSize]);

  const applyFilters = () => { setSkip(0); load(); };
  const resetFilters = () => {
    setFilters({ kind: "", ref: "", counterparty: "", currency: "", date_from: "", date_to: "" });
    setSkip(0);
    setTimeout(load, 0);
  };

  const currencies = ["USD", "CAD", "CLP", "DOP", "HTG", "EUR", "BRL", "MXN"];

  const exportCsv = () => {
    if (rows.length === 0) return;
    const cols = ["kind", "created_at", "ref_id", "sender", "recipient", "sent", "received", "fee", "note"];
    const lines = [cols.join(",")];
    for (const r of rows) {
      const p = fmtParty(r);
      const sent = r._kind === "external"
        ? `${r.type === "CREDIT" ? "+" : "-"}${r.amount}`
        : `${r.source_amount} ${r.source_currency}`;
      const received = r._kind === "external"
        ? `${r.balance_after}`
        : `${r.dest_amount} ${r.dest_currency}`;
      const fee = r.fee_usd > 0 ? `${r.fee_source_amount} ${r.fee_source_currency}` : "";
      lines.push([
        r._kind,
        r.created_at,
        r.ref_id || r.id,
        (p.from || "").replaceAll(",", ";"),
        (p.to || "").replaceAll(",", ";"),
        sent,
        received,
        fee,
        (r.note || "").replaceAll(",", ";").replaceAll("\n", " "),
      ].join(","));
    }
    const blob = new Blob([lines.join("\n")], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `funding-history-${Date.now()}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const totalPages = Math.max(1, Math.ceil(total / limit));
  const currentPage = Math.floor(skip / limit) + 1;

  return (
    <div className="space-y-6" data-testid="funding-history-page">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <div className="text-xs font-semibold uppercase tracking-widest text-slate-500">
            {t("nav.folder_my_wallet", "My Wallet")}
          </div>
          <h1
            className="text-3xl sm:text-4xl font-bold tracking-tight text-slate-900"
            style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}
          >
            Funding History
          </h1>
          <p className="text-slate-500 text-sm mt-1 max-w-2xl">
            Consolidated feed of every wallet movement — local currency transfers, internal wallet moves, and external funding.
            {scopeAll ? " Platform-wide view." : " Your account only."}
          </p>
        </div>
        <button
          onClick={exportCsv}
          disabled={rows.length === 0}
          data-testid="funding-history-export"
          className="inline-flex items-center gap-2 h-10 px-4 rounded-md border border-slate-300 bg-white text-slate-700 text-sm font-semibold hover:bg-slate-50 disabled:opacity-40"
        >
          <Download className="w-4 h-4" /> Export CSV
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4">
        <div className="flex items-center gap-2 mb-3">
          <Filter className="w-4 h-4 text-slate-500" />
          <div className="text-sm font-semibold text-slate-900">Filters</div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 lg:grid-cols-6 gap-3">
          <select
            value={filters.kind}
            onChange={(e) => setFilters((f) => ({ ...f, kind: e.target.value }))}
            data-testid="filter-kind"
            className="h-10 rounded-md border border-slate-300 px-2 text-sm bg-white"
          >
            <option value="">All kinds</option>
            <option value="local">Local Transfer</option>
            <option value="internal">Internal</option>
            <option value="external">External Funding</option>
          </select>
          <input
            value={filters.ref}
            onChange={(e) => setFilters((f) => ({ ...f, ref: e.target.value }))}
            data-testid="filter-ref"
            placeholder="Ref ID (partial)"
            className="h-10 rounded-md border border-slate-300 px-3 text-sm font-mono"
          />
          <input
            value={filters.counterparty}
            onChange={(e) => setFilters((f) => ({ ...f, counterparty: e.target.value }))}
            data-testid="filter-counterparty"
            placeholder="Counterparty (name/code)"
            className="h-10 rounded-md border border-slate-300 px-3 text-sm"
          />
          <select
            value={filters.currency}
            onChange={(e) => setFilters((f) => ({ ...f, currency: e.target.value }))}
            data-testid="filter-currency"
            className="h-10 rounded-md border border-slate-300 px-2 text-sm bg-white"
          >
            <option value="">All currencies</option>
            {currencies.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
          <input
            type="date"
            value={filters.date_from}
            onChange={(e) => setFilters((f) => ({ ...f, date_from: e.target.value }))}
            data-testid="filter-date-from"
            className="h-10 rounded-md border border-slate-300 px-2 text-sm"
          />
          <input
            type="date"
            value={filters.date_to}
            onChange={(e) => setFilters((f) => ({ ...f, date_to: e.target.value }))}
            data-testid="filter-date-to"
            className="h-10 rounded-md border border-slate-300 px-2 text-sm"
          />
        </div>
        <div className="flex justify-end gap-2 mt-3">
          <button
            onClick={resetFilters}
            className="h-9 px-3 rounded-md border border-slate-300 text-slate-600 text-xs font-semibold hover:bg-slate-50"
            data-testid="filter-reset"
          >
            Reset
          </button>
          <button
            onClick={applyFilters}
            data-testid="filter-apply"
            className="inline-flex items-center gap-1.5 h-9 px-4 rounded-md bg-[#0F62FE] text-white text-xs font-semibold hover:bg-[#0043A8]"
          >
            <Search className="w-3.5 h-3.5" /> Apply
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm">
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200">
          <div className="flex items-center gap-2">
            <History className="w-4 h-4 text-slate-500" />
            <div>
              <div className="text-sm font-semibold text-slate-900">Consolidated feed</div>
              <div className="text-xs text-slate-500 mt-0.5" data-testid="funding-history-total">
                {total} {total === 1 ? "row" : "rows"} · page {currentPage}/{totalPages}
              </div>
            </div>
          </div>
          <button
            onClick={load}
            className="inline-flex items-center gap-1.5 h-8 px-3 rounded-md border border-slate-300 text-slate-700 text-xs font-semibold hover:bg-slate-50"
          >
            <RefreshCw className="w-3.5 h-3.5" /> Reload
          </button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr>
                {[
                  { k: "kind", label: "Kind", align: "left" },
                  { k: "date", label: "Date", align: "left" },
                  { k: "ref", label: "Ref", align: "left" },
                  { k: "from", label: "From", align: "left" },
                  { k: "to", label: "To", align: "left" },
                  { k: "sent", label: "Sent", align: "right" },
                  { k: "received", label: "Received", align: "right" },
                  { k: "fee", label: "Fee", align: "right" },
                  { k: "note", label: "Note", align: "left" },
                  { k: "actions", label: "Actions", align: "left" },
                ].map((c) => (
                  <th
                    key={c.k}
                    className={`bg-slate-50 ${c.align === "right" ? "text-right" : "text-left"} text-[11px] font-semibold text-slate-500 uppercase tracking-wider px-4 py-3 border-b border-slate-200 whitespace-nowrap`}
                  >
                    {c.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={10} className="px-5 py-10 text-center text-slate-500 text-sm">
                  <Loader2 className="w-4 h-4 animate-spin inline-block mr-1.5" /> Loading…
                </td></tr>
              )}
              {!loading && rows.length === 0 && (
                <tr><td colSpan={10} className="px-5 py-10 text-center text-slate-500 text-sm">No records match the current filters.</td></tr>
              )}
              {!loading && rows.map((r) => {
                const p = fmtParty(r);
                const ref = r.ref_id || r.id;
                const canReceipt = r._kind === "local" || r._kind === "internal";
                return (
                  <tr key={`${r._kind}-${ref}`} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`funding-row-${ref}`}>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-0.5 rounded border text-[10px] font-bold uppercase tracking-widest ${KIND_STYLES[r._kind] || "bg-slate-100 text-slate-700"}`}>
                        {KIND_LABEL[r._kind] || r._kind}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-slate-700 whitespace-nowrap text-xs">
                      {r.created_at ? formatDate(r.created_at) : "—"}
                    </td>
                    <td className="px-4 py-3 font-mono text-[11px] text-slate-600 whitespace-nowrap" title={ref}>
                      {shortRef(ref, 10)}…
                    </td>
                    <td className="px-4 py-3 text-xs">
                      <div className="text-slate-800 font-semibold">{p.from}</div>
                      {p.fromEmail && <div className="text-slate-500 truncate max-w-[240px]" title={p.fromEmail}>{p.fromEmail}</div>}
                    </td>
                    <td className="px-4 py-3 text-xs">
                      <div className="text-slate-800 font-semibold">{p.to}</div>
                      {p.toEmail && <div className="text-slate-500 truncate max-w-[240px]" title={p.toEmail}>{p.toEmail}</div>}
                    </td>
                    <td className="px-4 py-3 font-semibold text-rose-700 whitespace-nowrap text-right">
                      {r._kind === "external"
                        ? `${r.type === "DEBIT" ? "− " : "+ "}${fmtCash(r.amount, "USD")}`
                        : `− ${fmtCash(r.source_amount, r.source_currency)}`}
                    </td>
                    <td className="px-4 py-3 font-semibold text-emerald-700 whitespace-nowrap text-right">
                      {r._kind === "external"
                        ? `bal ${fmtCash(r.balance_after, "USD")}`
                        : `+ ${fmtCash(r.dest_amount, r.dest_currency)}`}
                    </td>
                    <td className="px-4 py-3 font-semibold text-orange-700 whitespace-nowrap text-xs text-right">
                      {r.fee_usd > 0
                        ? `− ${fmtCash(r.fee_source_amount, r.fee_source_currency || r.source_currency)}`
                        : "—"}
                    </td>
                    <td className="px-4 py-3 text-slate-500 text-xs max-w-[240px] truncate" title={r.note || ""}>
                      {r.note || "—"}
                    </td>
                    <td className="px-4 py-3">
                      {canReceipt && (
                        <TransferReceiptActions
                          kind={r._kind}
                          refId={ref}
                          canDownload={canDownload}
                          canShare={canShare}
                        />
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {/* Pagination */}
        <Pagination
          page={Math.floor(skip / pageSize) + 1}
          pageSize={pageSize}
          total={total}
          onPage={(p) => setSkip((p - 1) * pageSize)}
          onPageSize={(n) => { setPageSize(n); setSkip(0); }}
          testId="funding-history-pagination"
        />
      </div>
    </div>
  );
}
